import {
  MemberLevel,
  OccType,
  SourceChannelType,
  CustomUnableBookStatus,
  EnableBookEnum,
} from '@/config/constant';
import BaseService from '@/api/checkInBase';
import DateJS, {
  format2DateStr,
  format2FullTimeStr,
  format2TimeStamp,
  format2TimeStr,
} from '@/utils/date';
import getRandomId from '@/utils/uuid';
import { isNil, parseFen2Yuan } from '@/utils/number';
import {
  ConfigRoomPrice,
  RentRoomInfo,
  RentTimeInfo,
  RoomCategory,
  StrategyItem,
  RoomPriceInfo,
  SourceChannelInfo,
  SelectRoomItem,
  RemarkInfo,
  RoomItem,
  NoticeInfo,
  CouponValue,
} from '@/redux/reducers/reservation/base.d';
import {
  ReducerState,
  CustomerInfo,
  MainState,
  CommonInfo,
  storeName,
} from '@/redux/reducers/reservation/createReserve';
import { renderIntlLabel } from '@/utils';

import { ReserveCreateAction } from './constant';
import {
  createActionMiddleware,
  getRentTimeInfo,
  getDayDepartureTime,
  flatPriceList,
  getBusinessDate,
  getChannelList,
  getRentDayArr,
  flatArrByKey,
  flatRoomList,
  getRoomItemData,
  getEmptyCouponData,
  filterArrByKey,
  findItemByKey,
  getLastAuditTime,
  batchCalcCouponValue,
  formatAssignRoomList,
} from './helper';

// getState函数覆写
const actionWrapper = createActionMiddleware<ReducerState>(storeName);

// 获取可选房间
const getAllRoomList: (timeInfo: RentTimeInfo) => Promise<RoomCategory[]> = async (timeInfo) => {
  let result: RoomCategory[] = [];
  try {
    const { expectedArrivalTime, rentTime, expectedDepartureTime } = timeInfo;
    if (expectedArrivalTime && rentTime) {
      const res = await BaseService.getRoomList({
        expectedArrivalTime: format2TimeStamp(expectedArrivalTime),
        expectedDepartureTime: format2TimeStamp(expectedDepartureTime),
      });
      result = formatAssignRoomList(res.data) || [];
      console.log(999);
    }
  } catch (_) {
    result = [];
  }
  return result;
};

// 获取房间定价
const getRoomPriceList: (
  state: ReducerState,
  roomList: RoomCategory[],
) => Promise<ConfigRoomPrice[]> = async (state, roomList) => {
  let result: ConfigRoomPrice[] = [];
  try {
    const { channelInfo, commonInfo, customerInfo } = state;
    if (roomList.length && channelInfo.channelId) {
      const timeInfo = getRentTimeInfo(state, commonInfo.occType);
      const roomTypeIds: number[] = []; // 去重的房型列表
      const typeIdMap: { [key: number]: boolean } = {}; // 房型id map
      (roomList || []).forEach((item) => {
        const { id } = item;
        // 已有的房型id
        if (typeIdMap[id]) return;
        roomTypeIds.push(id);
        typeIdMap[id] = true;
      });
      const res = await BaseService.getRoomPrice({
        channelId: channelInfo.channelId,
        endDate: format2DateStr(timeInfo.expectedDepartureTime),
        occDuration: commonInfo.occType === OccType.DayUse ? undefined : timeInfo.rentTime, // 日租房不传
        occType: commonInfo.occType,
        roomTypeIds,
        startDate: format2DateStr(timeInfo.businessDate), // 开始日期要传营业日 营业日小于等于当前日期
        strategyId: channelInfo.strategyId,
        wyuid: customerInfo.wyuId,
        memberLevel: commonInfo.memberLevel,
      });
      result = res.data || [];
    }
  } catch (_) {
    result = [];
  }
  return result;
};

// 获取可选策略
const getStrategyList: (state: MainState) => Promise<StrategyItem[]> = async (state) => {
  let result: StrategyItem[] = [];
  try {
    const { commonInfo, channelInfo } = state;
    if (channelInfo.channelId) {
      // 已经选择渠道了
      const timeInfo = getRentTimeInfo(state, commonInfo.occType);
      const res = await BaseService.getStrategyList({
        channelId: channelInfo.channelId,
        memberLevel: commonInfo.memberLevel,
        occDuration: timeInfo.rentTime,
        occType: commonInfo.occType,
      });
      result = res.data || [];
    }
  } catch (_) {
    result = [];
  }
  return result;
};

/**
 * 更新loading状态
 * 加个timer防止重复修改时闪烁
 */
let loadingTimer;
export const setLoadingStatus: (isLoading: boolean) => void = (isLoading) => {
  if (loadingTimer) {
    clearTimeout(loadingTimer);
  }
  return actionWrapper((dispatch, getState) => {
    // 和当前状态不一样
    loadingTimer = setTimeout(() => {
      loadingTimer = null;
      if (getState().isLoading !== isLoading) {
        dispatch({
          type: ReserveCreateAction.setLoadingStatus,
          payload: isLoading,
        });
      }
    });
  });
};

// 更新初始化状态
const setInitStatus = (isInit: boolean) => {
  return { type: ReserveCreateAction.setInitStatus, payload: isInit };
};

// 更新数据变更状态
const setModifyStatus = (isModify: boolean) => {
  return { type: ReserveCreateAction.setModifyStatus, payload: isModify };
};

// 更新策略列表
const setStrategyList = (data: StrategyItem[]) => {
  return {
    type: ReserveCreateAction.setStrategyList,
    payload: data,
  };
};

// 更新已选房间列表
const setSelectList = (list: SelectRoomItem[]) => {
  return {
    type: ReserveCreateAction.setCheckedList,
    payload: list,
  };
};

// 更新可选房间列表
const setAllList = (list: RoomCategory[]) => {
  return {
    type: ReserveCreateAction.setAllRoomList,
    payload: list,
  };
};

// 更新可用房间提示
const setRoomNotice = (notice: NoticeInfo) => {
  return {
    type: ReserveCreateAction.setRoomNotice,
    payload: notice,
  };
};

// 更新房价列表
const setPriceList = (list: ConfigRoomPrice[]) => {
  return {
    type: ReserveCreateAction.setRoomPriceList,
    payload: list,
  };
};

// 更新入住房间信息
const setRentList: (list: RentRoomInfo[]) => void = (list) => {
  return {
    type: ReserveCreateAction.setRentRoomList,
    payload: list,
  };
};

// 更新公共信息
const setCommonData: (data: Partial<CommonInfo>) => void = (data) => {
  return { type: ReserveCreateAction.setCommonData, payload: data };
};

// 更新预订人信息
const setCustomerData: (data: Partial<CustomerInfo>) => void = (data) => {
  return {
    type: ReserveCreateAction.setCustomerData,
    payload: data,
  };
};

// 更新日租房时间信息
const setDayTimeData: (time: Partial<RentTimeInfo>) => void = (time) => {
  return {
    type: ReserveCreateAction.setDayTimeData,
    payload: time,
  };
};

// 更新钟点房时间信息
const setHourTimeData: (time: Partial<RentTimeInfo>) => void = (time) => {
  return {
    type: ReserveCreateAction.setHourTimeData,
    payload: time,
  };
};

// 更新客源渠道信息
const setChannelData = (data: Partial<SourceChannelInfo>) => {
  return {
    type: ReserveCreateAction.setChannelData,
    payload: data,
  };
};

// 更新备注
const setRemarkInfo = (data: Partial<RemarkInfo>) => {
  return {
    type: ReserveCreateAction.setRemarkInfo,
    payload: data,
  };
};

/**
 * 清空房间信息
 * 1、清空已选列表
 * 2、清空房间列表
 * 3、清空入住列表
 * 3、清空房价列表
 */
const clearRoomInfo = () => {
  return (dispatch) => {
    dispatch(setSelectList([]));
    dispatch(setRentList([]));
    dispatch(setPriceList([]));
    dispatch(setAllList([]));
  };
};

// 清空房间优惠券
const clearCouponInfo = () => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos } = getState();
    if (rentRoomInfos && rentRoomInfos.length) {
      const emptyCouponData = getEmptyCouponData();
      dispatch(
        setRentList(
          rentRoomInfos.map((item) => {
            const priceList = item.roomPriceList || [];
            return {
              ...item,
              roomPriceList: priceList.map((dayPrice) => {
                return {
                  ...dayPrice,
                  ...emptyCouponData,
                };
              }),
            };
          }),
        ),
      );
    }
  });
};

// 选择入住房间
const handleSelectListChange: (list: SelectRoomItem[]) => void = (list) => {
  return actionWrapper(async (dispatch, getState) => {
    const selectList = list || [];
    const updateSelectList: SelectRoomItem[] = [];
    let rentRoomList: RentRoomInfo[] = [];
    if (selectList.length) {
      const state = getState();
      const { commonInfo, rentRoomInfos, allRoomList, roomPriceList } = state;
      const { occType } = commonInfo;
      const rentTimeInfo = getRentTimeInfo(state, occType);
      const rentDateArr = getRentDayArr({ rentTimeInfo, occType, rentRoomInfos });
      if (rentDateArr.length) {
        const selectIdMap = flatArrByKey(selectList, 'indexId'); // 选择的房间map
        // 过滤出之前已选中的入住房间
        const filterRentList = rentRoomInfos.filter((item) => {
          const roomItem = selectIdMap[item.indexId];
          if (roomItem) {
            // 房间之前已被选中 从选中的房间map中删掉
            updateSelectList.push(item);
            delete selectIdMap[item.indexId];
            return true;
          }
          return false;
        });

        // 价格列表为空 更新价格数据
        if (!roomPriceList.length) {
          dispatch(setLoadingStatus(true));
          const priceList = await getRoomPriceList(state, allRoomList);
          dispatch(setPriceList(priceList));
          dispatch(setLoadingStatus(false));
        }
        // 缓存的房价数据 转为map
        const { cachePriceMap, cacheDepositMap } = flatPriceList(getState().roomPriceList);
        const roomDataMap = flatRoomList(allRoomList);
        const emptyCouponData = getEmptyCouponData();
        Object.values(selectIdMap).forEach((item) => {
          const { indexId, roomId, roomTypeId } = item;
          const roomData = getRoomItemData(roomDataMap, roomTypeId, roomId);
          if (roomId != null && roomData.roomNo == null) {
            // 有房间id 但是没有房号 房间可能已经不在了
            return;
          }
          updateSelectList.push(item);
          const itemPriceList: RoomPriceInfo[] = [];
          const typePriceMap = cachePriceMap[roomTypeId] || {};
          rentDateArr.forEach((dayInfo) => {
            const { rentDateId, rentDate } = dayInfo;
            itemPriceList.push({
              rentDateId,
              occType,
              rentDate,
              inputPrice: typePriceMap[rentDate],
              ...emptyCouponData,
            });
          });
          filterRentList.push({
            indexId,
            deposit: cacheDepositMap[item.roomTypeId],
            // guestList: [],
            roomId,
            roomNo: roomData.roomNo,
            roomTypeId,
            roomTypeName: roomData.roomTypeName,
            roomPriceList: itemPriceList,
          });
        });
        rentRoomList = filterRentList;
      }
    }
    dispatch(setSelectList(updateSelectList));
    dispatch(setRentList(rentRoomList));
    dispatch(setModifyStatus(true)); // 数据修改标识
  });
};

// 刷新可选房间列表
const refreshRoomList = () => {
  return actionWrapper(async (dispatch, getState) => {
    const state = getState();
    const { commonInfo, defaultSelectRoom } = state;
    const rentTimeInfo = getRentTimeInfo(state, commonInfo.occType);
    const { expectedArrivalTime, rentTime } = rentTimeInfo;
    if (expectedArrivalTime && rentTime) {
      dispatch(setLoadingStatus(true));
      const roomList = await getAllRoomList(rentTimeInfo);
      const noticeInfo: NoticeInfo = { type: 'error' };
      const hasSubRoom = findItemByKey(roomList, (category) => {
        // 房型下有房间
        return category.roomList && category.roomList.length > 0;
      });
      if (!hasSubRoom) {
        // 无可用房间提示
        noticeInfo.message = renderIntlLabel(
          'reservation_no_avalible_room_to_reservation_period_of_time',
        );
      }
      dispatch(setRoomNotice(noticeInfo));
      dispatch(setAllList(roomList));
      if (hasSubRoom && defaultSelectRoom.length) {
        // 默认选中的房间
        dispatch(handleSelectListChange(defaultSelectRoom));
      }
      dispatch(setLoadingStatus(false));
    }
  });
};

// 刷新房间定价
const refreshPriceList = () => {
  return actionWrapper(async (dispatch, getState) => {
    const state = getState();
    const { allRoomList, rentRoomInfos } = state;
    // 没有选中房间
    if (!rentRoomInfos.length) return;
    dispatch(setLoadingStatus(true));
    const priceList = await getRoomPriceList(state, allRoomList || []);
    dispatch(setPriceList(priceList));
    const { cachePriceMap, cacheDepositMap } = flatPriceList(priceList);
    const emptyCouponData = getEmptyCouponData();
    // 更新已选房间房价
    dispatch(
      setRentList(
        rentRoomInfos.map((roomItem) => {
          const itemPriceList = roomItem.roomPriceList || [];
          const typePriceMap = cachePriceMap[roomItem.roomTypeId] || {};
          return {
            ...roomItem,
            deposit: cacheDepositMap[roomItem.roomTypeId],
            roomPriceList: itemPriceList.map((dayItem) => {
              return {
                ...dayItem,
                ...emptyCouponData, // 价格变化 已选优惠券清空
                inputPrice: typePriceMap[dayItem.rentDate],
              };
            }),
          };
        }),
      ),
    );
    dispatch(setLoadingStatus(false));
  });
};

// 获取默认所在营业日
const getDefaultBusinessDate: (callback: () => void) => void = (callback) => {
  return actionWrapper((dispatch, getState) => {
    const { dayTypeTimeInfo, hourTypeTimeInfo } = getState();
    dispatch(setLoadingStatus(true));
    const dayExpectedTime = dayTypeTimeInfo.expectedArrivalTime;
    const hourExpectedTime = hourTypeTimeInfo.expectedArrivalTime;
    const queryArr = [dayExpectedTime];
    const isNotSameDate =
      format2FullTimeStr(dayExpectedTime) !== format2FullTimeStr(hourExpectedTime);
    if (isNotSameDate) {
      queryArr.push(hourExpectedTime);
    }
    Promise.all(queryArr.map(getBusinessDate))
      .then(([dayBusinessDate, hourBusinessDate]) => {
        dispatch(
          setDayTimeData({
            businessDate: dayBusinessDate,
            expectedDepartureTime: getDayDepartureTime(
              { ...dayTypeTimeInfo, businessDate: dayBusinessDate },
              format2TimeStr(dayTypeTimeInfo.expectedDepartureTime),
            ),
          }),
        );
        dispatch(
          setHourTimeData({
            businessDate: isNotSameDate ? hourBusinessDate : dayBusinessDate,
          }),
        );
      })
      .then(() => {
        dispatch(setLoadingStatus(false));
        if (typeof callback === 'function') {
          callback();
        }
      });
  });
};

// 页面初始化
export const initPageState: (initFunc: () => Promise<MainState>) => void = (initFunc) => {
  return async (dispatch) => {
    try {
      // 未初始化状态
      dispatch({
        type: ReserveCreateAction.initDefaultState,
        payload: <ReducerState>{
          isInit: false,
          isLoading: true,
          isModify: false,
          channelList: [],
          strategyList: [],
          allRoomList: [],
          roomNoticeData: {},
          roomPriceList: [],
          selectRoomList: [],
          rentRoomInfos: [],
          commonInfo: {},
          customerInfo: {},
          dayTypeTimeInfo: {},
          hourTypeTimeInfo: {},
          channelInfo: {},
          remarkInfo: {},
          defaultSelectRoom: [],
        },
      });
      // 获取渠道信息
      getChannelList().then((channelList) => {
        dispatch({ type: ReserveCreateAction.setChannelList, payload: channelList });
      });
      // 调用初始化方法
      const initState = await initFunc();
      // 设置初始化数据
      dispatch({ type: ReserveCreateAction.initDefaultState, payload: initState });
      dispatch(setInitStatus(true));
      // 获取策略列表
      getStrategyList(initState).then((strategyList) => {
        dispatch(setStrategyList(strategyList));
      });
      getLastAuditTime().then((lastAuditDate) => {
        dispatch(setCommonData({ lastAuditDate }));
      });
      // 初始化所在营业日
      dispatch(
        getDefaultBusinessDate(() => {
          // 刷新可选房间列表
          dispatch(refreshRoomList());
        }),
      );
    } catch (_) {
      dispatch(setInitStatus(false));
    }
  };
};

// 用户信息修改
export const handleCustomerChange = (data: Partial<CustomerInfo>) => {
  return actionWrapper((dispatch) => {
    if ('wyuId' in data) {
      // 用户id变化 清空优惠券
      dispatch(clearCouponInfo());
    }
    dispatch(setCustomerData(data));
    dispatch(setModifyStatus(true));
  });
};

const setDayETDByStrategy: (
  strategy: StrategyItem,
  callback?: (isChange: boolean) => void,
) => void = (strategy, callback) => {
  return actionWrapper((dispatch, getState) => {
    const { dayTypeTimeInfo } = getState();
    let isETDChange = false;
    if (strategy?.departureDate) {
      // 根据策略的离店时间 计算日租房的离店时间
      const departureTime = getDayDepartureTime(dayTypeTimeInfo, strategy.departureDate);
      isETDChange =
        format2TimeStamp(departureTime) !== format2TimeStamp(dayTypeTimeInfo.expectedDepartureTime); // 离店时间改变
      if (isETDChange) {
        // 更新离店时间
        dispatch(setDayTimeData({ expectedDepartureTime: departureTime }));
      }
    }
    if (typeof callback === 'function') {
      callback(isETDChange);
    }
  });
};

// 预订人会员状态改变
export const handleMemberStatusChange: (memberLevel: MemberLevel) => void = (memberLevel) => {
  return actionWrapper(async (dispatch, getState) => {
    const { channelInfo, commonInfo, dayTypeTimeInfo } = getState();
    if (commonInfo.memberLevel === memberLevel) return; // 会员状态没有变化
    const isMember = !isNil(memberLevel) && memberLevel !== MemberLevel.Common;
    const isDayUse = commonInfo.occType === OccType.DayUse; // 是否是日租房
    // 设置会员状态
    dispatch(setCommonData({ memberLevel }));
    // 会员状态对应的日租房预离时间
    dispatch(
      setDayTimeData({
        expectedDepartureTime: getDayDepartureTime(
          dayTypeTimeInfo,
          isMember ? '14:00:00' : '12:00:00', // 会员14点 非会员12点
        ),
      }),
    );
    dispatch(setModifyStatus(true));
    if (channelInfo.channelId) {
      // 如果已选渠道 更新计价策略
      dispatch(setLoadingStatus(true));
      const strategyList = await getStrategyList(getState());
      dispatch(setStrategyList(strategyList));
      // 默认选中第一个策略
      const defaultStrategy = strategyList[0];
      dispatch(setChannelData({ strategyId: defaultStrategy?.id }));
      if (isDayUse) {
        // 根据策略变更离店时间
        dispatch(setDayETDByStrategy(defaultStrategy));
      }
      dispatch(setLoadingStatus(false));
    }

    // 离店时间是否改变
    // const isETDChange =
    //   format2TimeStamp(dayTypeTimeInfo.expectedDepartureTime) !==
    //   format2TimeStamp(getState().dayTypeTimeInfo.expectedDepartureTime);

    // if (isETDChange && isDayUse) {
    //   // 日租房离店时间改变 清空房间信息 刷新可选列表 更新房型价格
    //   dispatch(clearRoomInfo());
    //   dispatch(refreshRoomList());
    //   dispatch(refreshPriceList());
    // }

    dispatch(clearRoomInfo());
    dispatch(refreshRoomList());
  });
};

// 入住类型改变
export const handleOccTypeChange: (occType: OccType) => void = (occType) => {
  return actionWrapper(async (dispatch, getState) => {
    const { channelInfo } = getState();
    dispatch(setCommonData({ occType })); // 设置occType
    dispatch(setModifyStatus(true)); // 数据变动
    if (channelInfo.channelId) {
      dispatch(setLoadingStatus(true));
      // 更新计价策略
      const strategyList = await getStrategyList(getState());
      dispatch(setStrategyList(strategyList));
      // 默认选中第一个策略
      const defaultStrategy = strategyList[0];
      dispatch(setChannelData({ strategyId: defaultStrategy?.id }));
      if (occType === OccType.DayUse) {
        // 根据策略变更离店时间
        dispatch(setDayETDByStrategy(defaultStrategy));
      }
      dispatch(setLoadingStatus(false));
    }
    dispatch(clearRoomInfo()); // 清空已选房间
    dispatch(refreshRoomList());
  });
};

// 日租房时间改变
export const handleDayTimeChange: (data: Partial<RentTimeInfo>) => void = (data) => {
  return actionWrapper(async (dispatch, getState) => {
    const { dayTypeTimeInfo } = getState();
    const updateDate: RentTimeInfo = { ...dayTypeTimeInfo, ...data };
    if (
      data.expectedArrivalTime &&
      data.expectedArrivalTime !== dayTypeTimeInfo.expectedArrivalTime
    ) {
      // 预抵日期改变 更新所在营业日
      dispatch(setLoadingStatus(true));
      const businessDate = await getBusinessDate(data.expectedArrivalTime);
      updateDate.businessDate = businessDate;
      dispatch(setLoadingStatus(false));
    }
    updateDate.expectedDepartureTime = getDayDepartureTime(
      updateDate,
      format2TimeStr(updateDate.expectedDepartureTime),
    ); // 计算离店时间
    dispatch(setDayTimeData(updateDate)); // 更新时间信息
    dispatch(clearRoomInfo()); // 清空已选房间
    dispatch(refreshRoomList()); // 刷新可选房间
    dispatch(setModifyStatus(true)); // 数据修改标识
  });
};

// 钟点房时间改变
export const handleHourTimeChange: (data: Partial<RentTimeInfo>) => void = (data) => {
  return actionWrapper(async (dispatch, getState) => {
    const { channelInfo, hourTypeTimeInfo } = getState();
    const updateDate = { ...hourTypeTimeInfo, ...data };
    if (data.expectedArrivalTime) {
      // 预抵时间修改 拉取所在营业日
      dispatch(setLoadingStatus(true));
      const businessDate = await getBusinessDate(data.expectedArrivalTime);
      updateDate.businessDate = businessDate;
      dispatch(setLoadingStatus(false));
    }
    // 抵店时间和入住时长都有 更新离店时间
    if (updateDate.expectedArrivalTime && updateDate.rentTime) {
      updateDate.expectedDepartureTime = DateJS(updateDate.expectedArrivalTime).add(
        updateDate.rentTime,
        'hour',
      );
    }
    dispatch(setHourTimeData(updateDate)); // 更新时间信息
    dispatch(clearRoomInfo()); // 清空已选房间
    dispatch(refreshRoomList()); // 刷新可选房间
    dispatch(setModifyStatus(true)); // 数据修改标识
    if (data.rentTime && channelInfo.channelId) {
      // 入住时长修改 且已选渠道 更新计价策略
      dispatch(setLoadingStatus(true));
      const strategyList = await getStrategyList(getState());
      dispatch(setStrategyList(strategyList));
      // 默认选中第一个策略
      const defaultStrategy = strategyList[0];
      dispatch(setChannelData({ strategyId: defaultStrategy?.id }));
      dispatch(setLoadingStatus(false));
      // 钟点房入住时长改变 刷新房价
      dispatch(refreshPriceList());
    }
  });
};

// 渠道id改变
const handleChannelIdChange = (channelId) => {
  return actionWrapper(async (dispatch, getState) => {
    const { channelList, commonInfo } = getState();
    const targetChannel = channelList.find((item) => item.id === channelId);
    if (targetChannel && targetChannel.type !== SourceChannelType.OTA) {
      // 非OTA渠道 清空渠道单号 预付款 开票金额
      dispatch(
        setChannelData({
          channelId,
          strategyId: undefined,
          channelOrderNo: '',
          otaPaymentAmount: '',
          invoiceAmount: '',
        }),
      );
    } else {
      // 更新渠道 策略
      dispatch(setChannelData({ channelId, strategyId: undefined }));
    }
    dispatch(clearCouponInfo()); // 渠道修改(部分渠道不可用券) 清空已选优惠券
    // 更新计价策略
    dispatch(setLoadingStatus(true));
    const state = getState();
    const strategyList = await getStrategyList(state);
    dispatch(setStrategyList(strategyList));
    // 默认选中第一个策略
    const defaultStrategy = strategyList[0];
    dispatch(setChannelData({ channelId, strategyId: defaultStrategy?.id }));
    let isETDChange = false; // 离店时间是否改变
    if (commonInfo.occType === OccType.DayUse) {
      // 根据策略变更离店时间
      dispatch(
        setDayETDByStrategy(defaultStrategy, (isChange) => {
          isETDChange = isChange;
        }),
      );
    }
    dispatch(setLoadingStatus(false));
    if (isETDChange) {
      // 离店时间改变 清空已选列表 刷新可选列表
      dispatch(clearRoomInfo());
      dispatch(refreshRoomList());
    }
    // 更新房型价格
    dispatch(refreshPriceList());
  });
};

// 策略改变
const handleStrategyChange = (strategyId) => {
  return actionWrapper(async (dispatch, getState) => {
    const { commonInfo, strategyList } = getState();
    dispatch(setChannelData({ strategyId }));
    const selectItem = strategyList.find((item) => item.id === strategyId);
    let isETDChange = false; // 离店时间是否改变
    if (commonInfo.occType === OccType.DayUse) {
      // 根据策略变更离店时间
      dispatch(
        setDayETDByStrategy(selectItem, (isChange) => {
          isETDChange = isChange;
        }),
      );
    }
    if (isETDChange) {
      // 离店时间改变 清空已选列表 刷新可选列表
      dispatch(clearRoomInfo());
      dispatch(refreshRoomList());
    }
    // 更新房型价格
    dispatch(refreshPriceList());
  });
};

// 客源渠道修改
export const handleChannelDataChange: (data: Partial<SourceChannelInfo>) => void = (data) => {
  return (dispatch) => {
    const { channelId, strategyId } = data;
    if (channelId != null) {
      dispatch(handleChannelIdChange(channelId));
    } else if (strategyId != null) {
      dispatch(handleStrategyChange(strategyId));
    } else {
      dispatch(setChannelData(data));
    }
    dispatch(setModifyStatus(true)); // 数据修改标识
  };
};

// 自动排房
export const handleAutoAssign = () => {
  return actionWrapper(async (dispatch, getState) => {
    const state = getState();
    const { rentRoomInfos, commonInfo } = state;
    // 是否有待排房(房号为空)
    const hasEmpty = rentRoomInfos.some((item) => !item.roomId);
    if (hasEmpty) {
      const timeInfo = getRentTimeInfo(state, commonInfo.occType);
      const expectedArrivalTime = format2TimeStamp(timeInfo.expectedArrivalTime);
      const expectedDepartureTime = format2TimeStamp(timeInfo.expectedDepartureTime);
      dispatch(setLoadingStatus(true));
      const { occType } = commonInfo;
      try {
        const res = await BaseService.getAssignRoom({
          originType: 0,
          autoRoomPreassignVOList: rentRoomInfos.map((item) => {
            return {
              expectedArrivalTime,
              expectedDepartureTime,
              occType,
              roomId: item.roomId,
              roomTypeId: item.roomTypeId,
            };
          }),
        });
        const roomList = res?.data || [];
        const updateSelectList: SelectRoomItem[] = [];
        const validRoomsMap = {};
        roomList.forEach((item) => {
          if (item.roomNo) {
            const categoryList = validRoomsMap[item.roomTypeId] || [];
            categoryList.push(item);
            validRoomsMap[item.roomTypeId] = categoryList;
          }
        });
        const updateRentRoomInfo = rentRoomInfos.map((item) => {
          let result = item;
          if (!item.roomNo) {
            // 未排房
            const targetRoom = validRoomsMap[item.roomTypeId].shift();
            if (targetRoom) {
              const { roomId, roomTypeId, roomNo } = targetRoom;
              result = {
                ...item,
                roomId,
                roomTypeId,
                roomNo,
              };
            }
          }
          const { roomId, roomTypeId } = result;
          updateSelectList.push({ roomId, roomTypeId, indexId: item.indexId });
          return result;
        });
        dispatch(setSelectList(updateSelectList));
        dispatch(setRentList(updateRentRoomInfo));
      } catch (_) {
        //
      }
      dispatch(setLoadingStatus(false));
    }
  });
};

// 房型 全选 取消
export const handleAllRoomSelect: (checked: boolean) => void = (checked) => {
  return actionWrapper((dispatch, getState) => {
    let updateList: SelectRoomItem[] = [];
    if (checked) {
      const { allRoomList, selectRoomList } = getState();
      const selectIdMap = flatArrByKey(selectRoomList, 'roomId');

      // 过滤掉待排房
      updateList = filterArrByKey(selectRoomList, (room) => room.roomId != null);
      allRoomList.forEach((category) => {
        const { id, roomList } = category;
        // v2.21 过滤掉不可预订房间
        const availableRoomList = (roomList || []).filter(
          (room) => room?.bookingInfo?.enableBook !== EnableBookEnum.unableBook,
        );
        (availableRoomList || []).forEach((room) => {
          // 房间未被选中
          if (!selectIdMap[room.id]) {
            updateList.push({
              indexId: getRandomId(),
              roomId: room.id,
              roomTypeId: id,
            });
          }
        });
      });
    }
    dispatch(handleSelectListChange(updateList));
  });
};

// 房型 全选 取消
export const handleCategorySelect: (roomTypeId: number, checked: boolean) => void = (
  roomTypeId,
  checked,
) => {
  return actionWrapper((dispatch, getState) => {
    let updateList: SelectRoomItem[] = [];
    const { allRoomList, selectRoomList } = getState();
    if (checked) {
      const categoryData = findItemByKey(allRoomList, 'id', roomTypeId);
      // v2.21 subRoomList过滤掉不可预订房间
      const subRoomList = (categoryData?.roomList || []).filter(
        (room) =>
          room.unableBookStatus !== CustomUnableBookStatus.Full &&
          room.unableBookStatus !== CustomUnableBookStatus.Stop,
      );
      // 过滤掉当前房型下的待排房
      updateList = filterArrByKey(
        selectRoomList,
        (room) => room.roomTypeId !== roomTypeId || room.roomId != null,
      );
      const selectIdMap = flatArrByKey(updateList, 'roomId');
      (subRoomList || []).forEach((room) => {
        // 房间未被选中
        if (!selectIdMap[room.id]) {
          updateList.push({
            indexId: getRandomId(),
            roomId: room.id,
            roomTypeId,
          });
        }
      });
    } else {
      // 过滤掉当前房型下的房间
      updateList = filterArrByKey(selectRoomList, (room) => room.roomTypeId !== roomTypeId);
    }
    dispatch(handleSelectListChange(updateList));
  });
};

// 房间 选中 取消
export const handleRoomSelect: (item: RoomItem, checked: boolean) => void = (item, checked) => {
  return actionWrapper((dispatch, getState) => {
    const { selectRoomList } = getState();
    const { roomTypeId, id } = item;
    // 过滤掉当前房间
    const updateList = filterArrByKey(
      selectRoomList,
      (room) => room.roomTypeId !== roomTypeId || room.roomId !== id,
    );
    if (checked) {
      updateList.push({
        indexId: getRandomId(),
        roomId: id,
        roomTypeId,
      });
    }
    dispatch(handleSelectListChange(updateList));
  });
};

// 添加待排房
export const handleAddBlockedRoom: (roomTypeId: number, count: number) => void = (
  roomTypeId,
  count,
) => {
  return actionWrapper((dispatch, getState) => {
    const { selectRoomList } = getState();
    const updateList = selectRoomList.slice();
    for (let idx = 0; idx < count; ) {
      updateList.push({
        indexId: getRandomId(),
        roomTypeId,
      });
      idx += 1;
    }
    dispatch(handleSelectListChange(updateList));
  });
};

// 删除已选房间
export const handleRentItemDelete: (indexId: string) => void = (indexId) => {
  return actionWrapper((dispatch, getState) => {
    const { selectRoomList } = getState();
    dispatch(
      handleSelectListChange(filterArrByKey(selectRoomList, (room) => room.indexId !== indexId)),
    );
  });
};

// 入住房间修改
export const handleRentItemChange: (indexId: string, updateValue: Partial<RentRoomInfo>) => void = (
  indexId,
  updateValue,
) => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos } = getState();
    dispatch(
      setRentList(
        rentRoomInfos.map((item) => {
          if (item.indexId === indexId) {
            return {
              ...item,
              ...updateValue,
            };
          }
          return item;
        }),
      ),
    );
  });
};

// 更新备注信息
export const handleRemarkInfoChange: (data: Partial<RemarkInfo>) => void = (data) => {
  return actionWrapper((dispatch) => {
    dispatch(setRemarkInfo(data));
    dispatch(setModifyStatus(true));
  });
};

// 刷新过期房间数据
export const refreshOutdateRoomInfo = () => {
  return actionWrapper((dispatch) => {
    dispatch(clearRoomInfo());
    dispatch(refreshRoomList());
  });
};

// 批量修改房价
export const batchUpdateRoomPrice = (list: { indexId: string; inputPrice: string }[]) => {
  return actionWrapper(async (dispatch, getState) => {
    dispatch(setLoadingStatus(true));
    const { rentRoomInfos, commonInfo, customerInfo } = getState();
    // 更新的房价
    const updatePriceValueMap = {};
    list.forEach((item) => {
      updatePriceValueMap[item.indexId] = item.inputPrice;
    });
    const updateCouponInfoList = []; // 重新计算金额的券列表
    const updateCouponNoMap = {}; // 重新计算金额的券map
    rentRoomInfos.forEach((roomItem) => {
      const updatePrice = updatePriceValueMap[roomItem.indexId];
      if (!updatePrice) return; // 没有修改当前房间
      (roomItem.roomPriceList || []).forEach((dayItem) => {
        const itemCouponNo = dayItem.couponNo;
        // 间夜有使用券
        if (itemCouponNo) {
          updateCouponInfoList.push({
            roomPrice: updatePrice,
            lockCouponNo: itemCouponNo,
            businessDate: dayItem.rentDate,
          });
          updateCouponNoMap[itemCouponNo] = true;
        }
      });
    });
    const updateCouponValueMap = await batchCalcCouponValue(
      { wyuid: customerInfo.wyuId, occType: commonInfo.occType },
      updateCouponInfoList,
    );
    const emptyCouponData = getEmptyCouponData();
    // 更新价格和券
    dispatch(
      setRentList(
        rentRoomInfos.map((roomItem) => {
          const updatePrice = updatePriceValueMap[roomItem.indexId];
          if (!updatePrice) return roomItem; // 没有修改当前房间
          const itemPriceList = roomItem.roomPriceList || [];
          return {
            ...roomItem,
            roomPriceList: itemPriceList.map((dayItem) => {
              // 当前间夜券金额重新计算过
              if (updateCouponNoMap[dayItem.couponNo]) {
                // 券的新面额
                const couponFaceValue = updateCouponValueMap[dayItem.couponNo];
                return {
                  ...dayItem,
                  // 计算金额不为空 更新面值 否则清空优惠券信息
                  ...(couponFaceValue
                    ? {
                        couponAmount: couponFaceValue,
                        couponValue: parseFen2Yuan(couponFaceValue),
                      }
                    : emptyCouponData),
                  inputPrice: updatePrice,
                };
              }
              return {
                ...dayItem,
                inputPrice: updatePrice,
              };
            }),
          };
        }),
      ),
    );
    dispatch(setLoadingStatus(false));
  });
};

// 批量修改房间券
export const batchUpdateRoomCoupon = (data: Record<string, Record<string, CouponValue>>) => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos } = getState();
    // 更新券
    dispatch(
      setRentList(
        rentRoomInfos.map((roomItem) => {
          const roomCouponMap = data[roomItem.indexId];
          if (!roomCouponMap) return roomItem;
          const itemPriceList = roomItem.roomPriceList || [];
          return {
            ...roomItem,
            roomPriceList: itemPriceList.map((dayItem) => {
              // 当前间夜券有调整
              if (roomCouponMap[dayItem.rentDateId]) {
                return {
                  ...dayItem,
                  ...roomCouponMap[dayItem.rentDateId],
                };
              }
              return dayItem;
            }),
          };
        }),
      ),
    );
  });
};

// 获取全部状态
export const getMainState: (callback: (state: ReducerState) => void) => void = (callback) => {
  return actionWrapper((_, getState) => {
    try {
      callback(getState());
    } catch (e) {
      //
    }
  });
};
